// MARK://dhk8/admin-shell/pages/modules/IngredientsPage
// Insumos (Product type=INGREDIENT) — el "home" que faltaba. Antes solo se creaban
// en el onboarding (una vez) o inline en la receta; no había dónde listarlos/editarlos.
// CRUD por el grafo erp-products: catalog.listIngredients / createIngredient /
// updateProduct / deleteProduct. Es la materia prima que consumen las recetas
// (café, leche, azúcar…), con su unidad y costo por unidad (alimenta el costeo).
//
// UX/DS (Gustavo 2026-06-22): lista → DataTable + FilterBar + SummaryStrip;
// estado "sin costo" → StatusPill warn; borrar → useConfirm.
// Barrido ficha-de-detalle (Gustavo 2026-06-23): cada fila NAVEGA a su ficha
// /ingredients/:id (IngredientDetailPage), donde vive el EDITAR + lotes/movimientos/
// proveedores/historial. "Nuevo insumo" se queda como sheet seccionado (solo alta);
// presentaciones/proveedores/historial requieren un insumo ya creado, así que se
// gestionan desde la ficha. La data y los campos que el grafo acepta no cambian.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2 } from 'lucide-react'

import * as cat from '../../lib/clients/catalog.js'
import type { Product } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip, type StatCardProps } from '../../components/StatCard.js'
import StatusPill from '../../components/StatusPill.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import ImageUploader from '../../components/ImageUploader.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

function mxn(cents: number): string {
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

const PAGE_SIZE = 12

interface InsumoForm {
  name: string
  unit: string
  costPesos: string
  description: string
  categoryId: string
  imageUrl: string | null
  imageKey: string | null
  satProductKey: string
  satUnitKey: string
}
const emptyForm = (): InsumoForm => ({ name: '', unit: 'GR', costPesos: '', description: '', categoryId: '', imageUrl: null, imageKey: null, satProductKey: '', satUnitKey: '' })

function needsCost(p: Product): boolean {
  return (p.tags ?? []).includes('needs-attention:cost-0') || p.priceCents === 0
}

export default function IngredientsPage() {
  const { toast } = useToast()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const list = useAsync((s) => cat.listIngredients(s))
  const cats = useAsync((s) => cat.listCategories(s))
  const [busy, setBusy] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [form, setForm] = useState<InsumoForm>(emptyForm())
  const [err, setErr] = useState<string | null>(null)

  // Filtros / orden / paginación client-side.
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('')
  const [costFilter, setCostFilter] = useState('')
  const [sort, setSort] = useState<SortState | null>({ key: 'name', dir: 'asc' })
  const [page, setPage] = useState(1)

  const catName = (id: string) => cats.data?.find((c) => c.id === id)?.name ?? null

  function openCreate() { setForm(emptyForm()); setErr(null); setSheetOpen(true) }

  // Alta de insumo (el EDITAR de un insumo existente vive en su ficha
  // /ingredients/:id → botón Editar; la lista solo crea).
  async function save() {
    setErr(null)
    if (!form.name.trim()) { setErr('El nombre del insumo es obligatorio.'); return }
    const costCents = Math.max(0, Math.round((Number.parseFloat(form.costPesos) || 0) * 100))
    const name = form.name.trim()
    const unit = form.unit.trim() || 'GRM'
    const description = form.description.trim() || null
    const categoryId = form.categoryId || null
    const satProductKey = form.satProductKey.trim() || undefined
    const satUnitKey = form.satUnitKey.trim() || undefined
    setBusy(true)
    try {
      const created = await cat.createIngredient({ name, unit, costCents })
      if (description || categoryId || form.imageUrl || satProductKey || satUnitKey) {
        await cat.updateProduct({ ...created, name, unit, priceCents: costCents, description: description ?? undefined, categoryId: categoryId ?? '', imageUrl: form.imageUrl, satProductKey, satUnitKey })
      }
      toast('Insumo creado', 'success')
      setSheetOpen(false); list.reload()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo guardar el insumo.')
    } finally { setBusy(false) }
  }

  async function remove(p: Product) {
    const ok = await confirm({ title: '¿Eliminar insumo?', message: `Se eliminará "${p.name}". No se puede deshacer.`, tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    setBusy(true)
    try { await cat.deleteProduct(p.id); toast('Insumo eliminado', 'success'); list.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar el insumo.', 'danger') }
    finally { setBusy(false) }
  }

  // KPIs
  const all = list.data ?? []
  const kpis = useMemo((): StatCardProps[] => {
    const withoutCost = all.filter(needsCost).length
    return [
      { label: 'Insumos', value: all.length },
      { label: 'Con costo', value: all.length - withoutCost, tone: 'ok' },
      { label: 'Sin costo', value: withoutCost, tone: withoutCost > 0 ? 'warn' : 'neutral' },
    ]
  }, [all])

  // Filtro + orden client-side.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let rows = all.filter((p) => {
      if (q && !(p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q))) return false
      if (catFilter && (p.categoryId ?? '') !== catFilter) return false
      if (costFilter === 'missing' && !needsCost(p)) return false
      if (costFilter === 'ok' && needsCost(p)) return false
      return true
    })
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      rows = [...rows].sort((a, b) => {
        if (sort.key === 'cost') return (a.priceCents - b.priceCents) * dir
        if (sort.key === 'unit') return (a.unit ?? '').localeCompare(b.unit ?? '') * dir
        return a.name.localeCompare(b.name) * dir
      })
    }
    return rows
  }, [all, search, catFilter, costFilter, sort])

  const total = filtered.length
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const onFilterChange = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1) }

  const columns: DataTableColumn<Product>[] = [
    { key: 'name', header: 'Insumo', sortable: true, render: (p) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {p.imageUrl
          ? <img src={p.imageUrl} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
          : <span style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--pos-elevated)', flexShrink: 0 }} />}
        <span style={{ fontWeight: 500 }}>{p.name}</span>
      </div>
    ) },
    { key: 'cost', header: 'Costo', align: 'right', sortable: true, render: (p) => <span className="adm-mono">{mxn(p.priceCents)}</span> },
    { key: 'unit', header: 'Unidad', sortable: true, render: (p) => unitShort(p.unit) || '—' },
    { key: 'category', header: 'Categoría', render: (p) => catName(p.categoryId) ?? <span className="adm-muted">—</span> },
    { key: 'estado', header: 'Estado', render: (p) => needsCost(p) ? <StatusPill tone="warn" label="Sin costo" /> : <StatusPill tone="ok" label="Con costo" /> },
    { key: 'acciones', header: '', align: 'right', width: '1%', render: (p) => (
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
        <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy} onClick={() => navigate(`/ingredients/${p.id}`)} aria-label="Abrir insumo"><Pencil size={15} /></button>
        <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={() => remove(p)} aria-label="Eliminar insumo"><Trash2 size={15} /></button>
      </div>
    ) },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Insumos</h1>
          <p className="adm-page__sub">Materias primas de las recetas (café, leche, azúcar…) — nombre, unidad y costo por unidad</p>
        </div>
      </div>

      {(list.data || list.loading) && (
        <div style={{ margin: '4px 0 12px' }}>
          <SummaryStrip items={kpis} />
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable<Product>
          columns={columns}
          rows={pageRows}
          getRowKey={(p) => p.id}
          zebra
          sort={sort}
          onSortChange={(s) => { setSort(s); setPage(1) }}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          onRowClick={(p) => navigate(`/ingredients/${p.id}`)}
          emptyText="Aún no hay insumos. Crea el primero para poder costear recetas y controlar existencias."
          toolbar={<FilterBar
            search={search}
            onSearch={onFilterChange(setSearch)}
            searchPlaceholder="Buscar insumo o SKU…"
            filters={[
              { key: 'cat', label: 'Categoría', value: catFilter, onChange: onFilterChange(setCatFilter), options: [{ value: '', label: 'Todas' }, ...(cats.data ?? []).map((c) => ({ value: c.id, label: c.name }))] },
              { key: 'cost', label: 'Costo', value: costFilter, onChange: onFilterChange(setCostFilter), options: [{ value: '', label: 'Todos' }, { value: 'ok', label: 'Con costo' }, { value: 'missing', label: 'Sin costo' }] },
            ]}
            actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openCreate}><Plus size={16} /> Nuevo insumo</button>}
          />}
        />
      )}

      <SectionedSheet
        open={sheetOpen}
        title="Nuevo insumo"
        onClose={() => setSheetOpen(false)}
        footer={<>
          <button className="adm-btn" onClick={() => setSheetOpen(false)} disabled={busy}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}

        <FormSection title="Identidad">
          <label className="adm-field">
            <span className="adm-field__label">Nombre</span>
            <input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Café en grano, Leche entera, Azúcar…" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Descripción</span>
            <textarea className="adm-textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notas del insumo: marca, presentación, proveedor habitual…" />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Categoría</span>
            <select className="adm-select" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
              <option value="">Sin categoría</option>
              {(cats.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        </FormSection>

        <FormSection title="Precio y unidad">
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <UnitPicker label="Unidad" value={form.unit} onChange={(unit) => setForm({ ...form, unit })} />
            </div>
            <label className="adm-field" style={{ flex: 1 }}>
              <span className="adm-field__label">Costo por unidad (MXN)</span>
              <input className="adm-input" inputMode="decimal" min={0} step="0.01" value={form.costPesos} onChange={(e) => setForm({ ...form, costPesos: e.target.value })} placeholder="0.00" />
            </label>
          </div>
        </FormSection>

        <FormSection title="Claves SAT (CFDI)">
          <div style={{ display: 'flex', gap: 8 }}>
            <label className="adm-field" style={{ flex: 1 }}>
              <span className="adm-field__label">Clave de producto/servicio</span>
              <input className="adm-input" value={form.satProductKey} onChange={(e) => setForm({ ...form, satProductKey: e.target.value })} placeholder="ej. 50202306" />
            </label>
            <div style={{ flex: 1 }}>
              <UnitPicker mode="sat" label="Clave de unidad" value={form.satUnitKey} onChange={(satUnitKey) => setForm({ ...form, satUnitKey })} />
            </div>
          </div>
        </FormSection>

        <FormSection title="Media">
          <div className="adm-field">
            <span className="adm-field__label">Imagen del insumo</span>
            <ImageUploader value={form.imageUrl} onChange={(url) => setForm({ ...form, imageUrl: url, imageKey: url ? form.imageKey : null })} />
          </div>
        </FormSection>

        <p className="adm-row__meta" style={{ marginTop: 4 }}>Presentaciones de compra, proveedores e historial se gestionan en la ficha del insumo tras crearlo.</p>
      </SectionedSheet>
    </div>
  )
}
